import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { FaPaperclip, FaTimesCircle, FaFile, FaReply, FaCheck, FaCheckDouble, FaArrowsAltH } from "react-icons/fa";
import { IoMdSend } from "react-icons/io";
import { formatChatDate, formatChatTime, shouldShowDateSeparator } from "../../utils/dateUtils";
import {
  CHAT_HISTORY_LIMIT,
  appendSelectedFiles,
  emitChatTyping,
  emitFileUploading,
  mergeUniqueMessages,
  normalizeMessageFiles,
  parseHistoryResponse,
  truncateFileName,
} from "../../utils/chatHelpers";
import ChatFileBubble from "../../components/chat/ChatFileBubble";
import ChatReminderPanel from "../../components/chat/ChatReminderPanel";
import { useEmployee } from "../../context/EmployeeContext";
import io from "socket.io-client";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const CHATBOX_SIZE_KEY = 'employeeChatboxSize';
const DEFAULT_CHATBOX_SIZE = { width: 380, height: 520 };

const WIDTH_PRESETS = [
  { id: 'compact', label: 'S', width: 340, title: 'Compact' },
  { id: 'normal', label: 'M', width: 380, title: 'Normal' },
  { id: 'wide', label: 'L', width: 480, title: 'Wide' },
  { id: 'xlarge', label: 'XL', width: 600, title: 'Extra wide' },
];

const getMaxChatboxWidth = () => Math.min(720, Math.floor(window.innerWidth * 0.92));
const getMaxChatboxHeight = () => Math.min(800, window.innerHeight - 96);

const clampChatboxSize = (size) => {
  const maxWidth = getMaxChatboxWidth();
  const maxHeight = getMaxChatboxHeight();
  return {
    width: Math.max(300, Math.min(maxWidth, size?.width || DEFAULT_CHATBOX_SIZE.width)),
    height: Math.max(420, Math.min(maxHeight, size?.height || DEFAULT_CHATBOX_SIZE.height)),
  };
};

const getActiveWidthPreset = (width) => {
  const preset = [...WIDTH_PRESETS]
    .reverse()
    .find((item) => width >= item.width - 20);
  return preset?.id || 'custom';
};

const saveChatboxSize = (size) => {
  localStorage.setItem(CHATBOX_SIZE_KEY, JSON.stringify(size));
};

const EmployeeChatbox = ({ user, onClose }) => {
  const { employee: loggedInEmployee } = useEmployee();
  const agentDisplayName = loggedInEmployee?.name?.trim() || 'BooksComply Support';
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [error, setError] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [sendEmailOnSend, setSendEmailOnSend] = useState(true);
  const [sendProgress, setSendProgress] = useState(0);
  const [userTyping, setUserTyping] = useState(false);
  const [isUserUploading, setIsUserUploading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [onlineUserIds, setOnlineUserIds] = useState([]);
  const [activeChatUserIds, setActiveChatUserIds] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [chatboxSize, setChatboxSize] = useState(DEFAULT_CHATBOX_SIZE);
  const [showWidthMenu, setShowWidthMenu] = useState(false);
  const fileInputRef = useRef(null);
  const socketRef = useRef(null);
  const resizeRef = useRef({ isResizing: false, startX: 0, startY: 0, startWidth: 0, startHeight: 0 });
  const widthMenuRef = useRef(null);
  const textareaRef = useRef(null);
  const sendingLock = useRef(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const activeWidthPreset = getActiveWidthPreset(chatboxSize.width);

  const applyChatboxSize = (nextSize) => {
    const clamped = clampChatboxSize(nextSize);
    setChatboxSize(clamped);
    saveChatboxSize(clamped);
  };

  const applyWidthPreset = (width) => {
    applyChatboxSize({ ...chatboxSize, width });
    setShowWidthMenu(false);
  };

  useEffect(() => {
    if (!showWidthMenu) return undefined;

    const handleClickOutside = (event) => {
      if (widthMenuRef.current && !widthMenuRef.current.contains(event.target)) {
        setShowWidthMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showWidthMenu]);

  // Resize functionality
  const handleTextareaChange = (e) => {
    const textarea = e.target;
    textarea.style.height = 'auto';
    const newHeight = textarea.scrollHeight;
    const maxHeight = 120; // Same as maxHeight in style
    textarea.style.height = newHeight > maxHeight ? maxHeight + 'px' : newHeight + 'px';
    setNewMessage(textarea.value);
    
    emitChatTyping(socketRef.current, {
      role: 'employee',
      targetUserId: user._id,
      isTyping: textarea.value.length > 0,
    });
  };

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 1024);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mark messages as read when chatbox opens
  const markMessagesAsRead = useCallback(async () => {
    if (!user) return;
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/mark-as-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user._id, userType: 'employee' }),
      });
    } catch (err) { console.error('Mark as read error:', err); }
  }, [user]);

  // Mark messages as delivered when employee opens chat
  const markMessagesAsDelivered = useCallback(async () => {
    if (!user) return;
    try {
      await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/chat/mark-as-delivered`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user._id, userType: 'employee' }),
      });
    } catch (err) { console.error('Mark as delivered error:', err); }
  }, [user]);

  // Get tick icon for read receipts
  const getTickIcon = (msg) => {
    if (msg.sender !== 'admin') return null;
    if (msg.pending) return null;
    if (msg.failed) return null;

    if (msg.isRead || msg.readAt) {
      return <FaCheckDouble style={{ color: '#3b82f6', fontSize: '11px', marginLeft: '4px' }} />;
    }
    if (msg.deliveredAt) {
      return <FaCheckDouble style={{ fontSize: '11px', marginLeft: '4px', opacity: 0.6 }} />;
    }
    return <FaCheck style={{ fontSize: '11px', marginLeft: '4px', opacity: 0.6 }} />;
  };

  // Initialize Socket.IO connection
  useEffect(() => {
    if (!user) return;
    
    socketRef.current = io(process.env.REACT_APP_API_BASE_URL);
    socketRef.current.emit('joinRoom', 'admin');
    socketRef.current.emit('getOnlineUsers');

    socketRef.current.on('onlineUsersList', (userIds) => {
      setOnlineUserIds(userIds || []);
    });

    socketRef.current.on('activeChatUsersList', (userIds) => {
      setActiveChatUserIds(userIds || []);
    });

    socketRef.current.on('userOnlineStatus', ({ userId, isOnline }) => {
      setOnlineUserIds(prev => {
        if (isOnline) {
          return prev.includes(userId) ? prev : [...prev, userId];
        } else {
          return prev.filter(id => id !== userId);
        }
      });
    });

    socketRef.current.on('newMessage', (message) => {
      if (message.userId === user._id) {
        setMessages(prevMessages => [...prevMessages, message]);
        markMessagesAsRead();
      }
    });
    
    socketRef.current.on('userTyping', ({ userId, isTyping }) => {
      if (userId === user._id) {
        setUserTyping(isTyping);
      }
    });

    socketRef.current.on('fileUploading', ({ userId, isUploading }) => {
      if (userId === user._id) {
        setIsUserUploading(isUploading);
      }
    });

    // Listen for read receipts from user
    socketRef.current.on('messagesRead', ({ userId, readAt, readBy }) => {
      if (userId === user._id && readBy === 'user') {
        setMessages(prev => prev.map(msg =>
          msg.sender === 'admin' && !msg.readAt
            ? { ...msg, isRead: true, readAt }
            : msg
        ));
      }
    });

    // Listen for delivery receipts
    socketRef.current.on('messagesDelivered', ({ userId, deliveredAt }) => {
      if (userId === user._id) {
        setMessages(prev => prev.map(msg =>
          msg.sender === 'admin' && !msg.deliveredAt
            ? { ...msg, deliveredAt }
            : msg
        ));
      }
    });

    // Listen for message edits
    socketRef.current.on('messageEdited', ({ messageId, newMessage, isEdited, editedAt, userId }) => {
      if (userId === user._id) {
        setMessages(prev => prev.map(msg =>
          msg._id === messageId
            ? { ...msg, message: newMessage, isEdited, editedAt }
            : msg
        ));
      }
    });

    // Mark existing messages as read & delivered on open
    markMessagesAsRead();
    markMessagesAsDelivered();
    
    return () => {
      socketRef.current.disconnect();
    };
  }, [user, markMessagesAsRead, markMessagesAsDelivered]);

  useEffect(() => {
    if (!shouldAutoScrollRef.current) return;
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchChatHistory = useCallback(async (before) => {
    if (!user?._id) return;
    const params = new URLSearchParams({
      userId: user._id,
      limit: String(CHAT_HISTORY_LIMIT),
    });
    if (before) params.set('before', before);

    const response = await fetch(`${API_BASE}/api/chat/history?${params}`);
    if (!response.ok) throw new Error('Failed to fetch chat history.');
    const data = await response.json();
    const { messages: batch, hasMore: more } = parseHistoryResponse(data);
    return { batch, hasMore: more };
  }, [user?._id]);

  useEffect(() => {
    if (!user) return;

    const loadInitial = async () => {
      try {
        const { batch, hasMore: more } = await fetchChatHistory();
        setMessages(batch);
        setHasMore(more);
        shouldAutoScrollRef.current = true;
      } catch (err) {
        setError(err.message);
      }
    };

    loadInitial();
  }, [user, fetchChatHistory]);

  const loadOlderMessages = async () => {
    if (!hasMore || loadingMore || messages.length === 0) return;
    setLoadingMore(true);
    shouldAutoScrollRef.current = false;
    const container = messagesContainerRef.current;
    const prevHeight = container?.scrollHeight || 0;

    try {
      const { batch, hasMore: more } = await fetchChatHistory(messages[0].timestamp);
      setMessages((prev) => mergeUniqueMessages(batch, prev));
      setHasMore(more);
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = container.scrollHeight - prevHeight;
        }
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleMessagesScroll = (event) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    shouldAutoScrollRef.current = scrollHeight - scrollTop - clientHeight < 80;
    if (scrollTop < 40) {
      loadOlderMessages();
    }
  };

  const handleReply = (message) => {
    setReplyingTo(message);
    setNewMessage(""); // Clear current message when starting reply
  };

  const cancelReply = () => {
    setReplyingTo(null);
  };

  const handleMessageSend = async () => {
    // Prevent multiple sends by checking lock
    if (sendingLock.current) return;
    
    if (newMessage.trim() !== "" || selectedFiles.length > 0) {
      // Set lock to prevent multiple sends
      sendingLock.current = true;
      setIsSending(true);
      setSendProgress(10); // Start progress
      
      try {
        if (selectedFiles.length > 0) {
          setSendProgress(30);
          await uploadFiles();
          setSendProgress(70);
          if (newMessage.trim() !== "") {
            // Already handled by uploadFiles
          }
        } else {
          setSendProgress(50);
          await sendTextMessage();
        }
        setSendProgress(100);
        
        // Reset progress after completion
        setTimeout(() => {
          setIsSending(false);
          setSendProgress(0);
        }, 500);
      } catch (error) {
        setError("Failed to send message. Please try again.");
        setIsSending(false);
        setSendProgress(0);
      } finally {
        // Release lock after sending completes or fails
        sendingLock.current = false;
      }
    }
  };

  const sendTextMessage = async () => {
    const messageText = newMessage.trim();
    if (!messageText) return;

    const tempId = `temp-${Date.now()}`;
    const replyTarget = replyingTo;
    const instantMessage = {
      _id: tempId,
      user: agentDisplayName,
      message: messageText,
      sender: "admin",
      timestamp: new Date().toISOString(),
      userId: user._id,
      pending: true,
      replyTo: replyTarget || null,
      employeeId: loggedInEmployee?._id || null,
    };

    shouldAutoScrollRef.current = true;
    setMessages((prev) => [...prev, instantMessage]);
    setNewMessage("");
    setReplyingTo(null);
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    const newMessageObj = {
      user: agentDisplayName,
      message: messageText,
      sender: "admin",
      timestamp: new Date().toISOString(),
      userId: user._id,
      replyTo: replyTarget ? replyTarget._id : null,
      sendEmail: sendEmailOnSend,
    };

    try {
      const response = await fetch(`${API_BASE}/api/chat/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("employeeToken")}`,
        },
        body: JSON.stringify(newMessageObj),
      });

      if (!response.ok) throw new Error("Failed to send message.");

      const data = await response.json();
      setMessages((prev) => prev.map((msg) => (msg._id === tempId ? { ...data, pending: false } : msg)));
      emitChatTyping(socketRef.current, { role: 'employee', targetUserId: user._id, isTyping: false });
    } catch (err) {
      setMessages((prev) => prev.map((msg) => (
        msg._id === tempId ? { ...msg, failed: true, pending: false } : msg
      )));
      setError(err.message);
    }
  };

  const processFiles = (files) => {
    const { files: nextFiles, error: fileError } = appendSelectedFiles(selectedFiles, files);
    if (fileError) {
      setError(fileError);
      return;
    }
    setSelectedFiles(nextFiles);
    setError('');
  };

  const handleFileChange = (e) => {
    processFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isMobile) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (!isMobile) processFiles(Array.from(e.dataTransfer.files || []));
  };

  const removeSelectedFile = (index) => {
    const updatedFiles = [...selectedFiles];
    updatedFiles.splice(index, 1);
    setSelectedFiles(updatedFiles);
    if (updatedFiles.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadFiles = async () => {
    const token = localStorage.getItem("employeeToken");
    const formData = new FormData();
    const batchId = Date.now();

    const tempMessages = selectedFiles.map((file) => ({
      _id: `temp-file-${batchId}-${file.name}`,
      user: agentDisplayName,
      message: `File uploaded: ${file.name}`,
      sender: "admin",
      timestamp: new Date().toISOString(),
      originalName: file.name,
      userId: user._id,
      pending: true,
      employeeId: loggedInEmployee?._id || null,
    }));

    shouldAutoScrollRef.current = true;
    setMessages((prev) => [...prev, ...tempMessages]);
    emitFileUploading(socketRef.current, { role: 'employee', userId: user._id, isUploading: true });

    selectedFiles.forEach((file) => formData.append("files", file));
    formData.append("user", agentDisplayName);
    formData.append("sender", "admin");
    formData.append("userId", user._id);
    formData.append("replyTo", replyingTo ? replyingTo._id : "");
    if (newMessage.trim()) formData.append("message", newMessage.trim());
    formData.append("sendEmail", sendEmailOnSend ? "true" : "false");

    try {
      const response = await fetch(`${API_BASE}/api/chat/upload`, {
        method: "POST",
        body: formData,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to upload files.");
      }

      const data = await response.json();
      setMessages((prev) => {
        const filtered = prev.filter((msg) => !String(msg._id).startsWith(`temp-file-${batchId}-`));
        return [...filtered, ...(Array.isArray(data) ? data : [data])];
      });
      setSelectedFiles([]);
      setNewMessage("");
      setReplyingTo(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      setMessages((prev) => prev.map((msg) => (
        String(msg._id).startsWith(`temp-file-${batchId}-`)
          ? { ...msg, failed: true, pending: false }
          : msg
      )));
      setError(err.message);
    } finally {
      emitFileUploading(socketRef.current, { role: 'employee', userId: user._id, isUploading: false });
    }
  };

  const handleMouseDown = (e) => {
    resizeRef.current.isResizing = true;
    resizeRef.current.startX = e.clientX;
    resizeRef.current.startY = e.clientY;
    resizeRef.current.startWidth = chatboxSize.width;
    resizeRef.current.startHeight = chatboxSize.height;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'nw-resize';
    document.body.style.userSelect = 'none';

    e.preventDefault();
  };

  const handleMouseMove = (e) => {
    if (!resizeRef.current.isResizing) return;

    const deltaX = e.clientX - resizeRef.current.startX;
    const deltaY = e.clientY - resizeRef.current.startY;

    // Invert deltaX to make left drag increase width and right drag decrease width
    const maxWidth = getMaxChatboxWidth();
    const maxHeight = getMaxChatboxHeight();
    const newWidth = Math.max(300, Math.min(maxWidth, resizeRef.current.startWidth - deltaX));
    const newHeight = Math.max(420, Math.min(maxHeight, resizeRef.current.startHeight - deltaY));

    setChatboxSize({ width: newWidth, height: newHeight });
  };

  const handleMouseUp = () => {
    resizeRef.current.isResizing = false;

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    // Save size preference to localStorage
    saveChatboxSize(chatboxSize);
  };

  useEffect(() => {
    const handleResize = () => {
      setChatboxSize((prev) => clampChatboxSize(prev));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load saved size preference on component mount
  useEffect(() => {
    const savedSize = localStorage.getItem(CHATBOX_SIZE_KEY);
    if (savedSize) {
      try {
        setChatboxSize(clampChatboxSize(JSON.parse(savedSize)));
      } catch (error) {
        console.error('Error parsing saved chatbox size:', error);
      }
    }
  }, []);

  if (!user || typeof document === 'undefined') return null;

  const shellClass = isMobile
    ? 'fixed inset-0 z-[1200] flex flex-col bg-white dark:bg-neutral-900 font-sans'
    : 'fixed bottom-4 right-4 z-[1200] flex flex-col bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden font-sans';

  return createPortal(
    <>
      {!isMobile && <div className="fixed inset-0 z-[1190] bg-black/20 backdrop-blur-[1px]" onClick={onClose} aria-hidden="true" />}
      <div
        id="employee-chatbox"
        className={shellClass}
        style={isMobile ? undefined : {
          width: chatboxSize.width,
          height: chatboxSize.height,
          maxWidth: 'calc(100vw - 1.5rem)',
          maxHeight: 'calc(100vh - 6rem)',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
      {isDragging && (
        <div className="absolute inset-0 z-20 bg-primary-500/10 backdrop-blur-[1px] flex items-center justify-center pointer-events-none">
          <div className="px-6 py-4 rounded-2xl bg-white dark:bg-neutral-900 shadow-xl border-2 border-dashed border-primary-500 text-sm font-semibold">
            Drop files to share
          </div>
        </div>
      )}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-black via-neutral-900 to-primary-900 text-white border-b border-white/10 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="relative shrink-0">
            {user.profilePicture ? (
              <img
                src={user.profilePicture}
                alt={user.name}
                className="w-10 h-10 rounded-full object-cover border border-white/20"
                onError={(e) => { e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=random&color=fff`; }}
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-secondary-500 flex items-center justify-center font-bold text-lg shrink-0">
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
            {onlineUserIds.includes(user._id) && (
              <span
                className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-400 border-2 border-neutral-900 rounded-full animate-pulse"
                title="Online Now"
              />
            )}
          </div>
          <div className="min-w-0">
            <h5 className="m-0 text-base font-semibold truncate">Chat with {user.name}</h5>
            {activeChatUserIds.includes(user._id) ? (
              <small className="text-emerald-300 font-bold text-xs flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Active now
              </small>
            ) : onlineUserIds.includes(user._id) ? (
              <small className="text-emerald-200/90 font-medium text-xs flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Online on platform
              </small>
            ) : (
              <small className="text-white/50 text-xs">Offline</small>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <div className="relative" ref={widthMenuRef}>
            <button
              type="button"
              onClick={() => setShowWidthMenu((prev) => !prev)}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/80 hover:text-white"
              title="Chat width"
              aria-label="Chat width options"
            >
              <FaArrowsAltH className="w-4 h-4" />
            </button>
            {showWidthMenu && (
              <div className="absolute right-0 top-full mt-1 w-44 bg-white dark:bg-neutral-900 rounded-xl shadow-2xl border border-gray-200 dark:border-neutral-700 py-2 z-10">
                <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Chat Width
                </p>
                <div className="px-2 pb-1 grid grid-cols-4 gap-1">
                  {WIDTH_PRESETS.map((preset) => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => applyWidthPreset(preset.width)}
                      title={`${preset.title} (${preset.width}px)`}
                      className={`py-1.5 rounded-lg text-xs font-bold transition-colors ${
                        activeWidthPreset === preset.id
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-neutral-700'
                      }`}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                <p className="px-3 pt-1 text-[10px] text-gray-400 dark:text-gray-500">
                  Current: {chatboxSize.width}px · drag top-left to resize
                </p>
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-white/80 hover:text-white" aria-label="Close chat">
            <FaTimesCircle className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        className="flex-1 overflow-y-auto p-4 bg-gray-50 dark:bg-black space-y-3"
      >
        {loadingMore && (
          <div className="text-center py-2 text-xs text-gray-500">Loading older messages...</div>
        )}
        {error && (
          <div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm border border-red-100 dark:border-red-900/30">
            {error}
          </div>
        )}

        {messages.map((msg, index) => {
          const showDateSeparator = shouldShowDateSeparator(msg, messages[index - 1]);
          const isAdmin = msg.sender === 'admin';
          const files = normalizeMessageFiles(msg);
          const showText = msg.message && !(files.length && msg.message.startsWith('File uploaded:'));

          return (
            <React.Fragment key={msg._id || `msg-${index}`}>
              {showDateSeparator && (
                <div className="flex justify-center my-2">
                  <span className="px-3 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 bg-white dark:bg-neutral-800 rounded-full border border-gray-200 dark:border-neutral-700">
                    {formatChatDate(msg.timestamp)}
                  </span>
                </div>
              )}
              <div className={`flex ${isAdmin ? 'justify-end' : 'justify-start'} ${msg.pending ? 'opacity-60' : ''} ${msg.failed ? 'opacity-40' : ''}`}>
                <div className={`group relative max-w-[85%] rounded-2xl px-4 py-2.5 shadow-sm ${
                  isAdmin
                    ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-br-md'
                    : 'bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-neutral-700 rounded-bl-md'
                }`}>
                  <button
                    onClick={() => handleReply(msg)}
                    className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 p-1.5 rounded-full bg-gray-100 dark:bg-neutral-700 text-gray-500 hover:text-primary-500 transition-all shadow-sm"
                    title="Reply"
                  >
                    <FaReply className="w-3 h-3" />
                  </button>

                  {msg.replyTo && msg.replyTo.user && msg.replyTo.message && (
                    <div className={`text-xs mb-2 pb-2 border-b ${isAdmin ? 'border-white/20 text-white/70' : 'border-gray-200 dark:border-neutral-600 text-gray-500'}`}>
                      Replying to {msg.replyTo.user}: {msg.replyTo.message?.substring(0, 50)}...
                    </div>
                  )}

                  {showText && <p className="text-sm m-0 whitespace-pre-wrap break-words">{msg.message}</p>}

                  {files.length > 0 && (
                    <div className={`flex flex-col gap-2 ${showText ? 'mt-2' : ''}`}>
                      {files.map((file, fileIndex) => (
                        <ChatFileBubble
                          key={`${msg._id}-file-${fileIndex}`}
                          file={file}
                          isOwn={isAdmin}
                          apiBaseUrl={API_BASE}
                        />
                      ))}
                    </div>
                  )}

                  <div className={`flex items-center gap-1 mt-1 ${isAdmin ? 'justify-end text-white/70' : 'text-gray-400'}`}>
                    {msg.isEdited && <span className="text-[10px] italic">edited</span>}
                    <span className="text-[10px]">
                      {msg.pending ? 'Sending...' : msg.failed ? 'Failed' : formatChatTime(msg.timestamp)}
                    </span>
                    {isAdmin && getTickIcon(msg)}
                  </div>
                </div>
              </div>
            </React.Fragment>
          );
        })}

        {userTyping && (
          <div className="flex justify-start">
            <div className="px-4 py-2 rounded-2xl bg-white dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700">
              <div className="flex items-center gap-1">
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        {isUserUploading && (
          <p className="text-xs text-gray-500 dark:text-gray-400 italic px-2">User is sending files...</p>
        )}

        {isSending && (
          <div className="h-1 bg-gray-200 dark:bg-neutral-800 rounded-full overflow-hidden">
            <div className="h-full bg-primary-500 transition-all duration-300" style={{ width: `${sendProgress}%` }} />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <ChatReminderPanel userId={user._id} tokenKey="employeeToken" defaultMessage={newMessage} />
      <div className="border-t border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3">
        {replyingTo && (
          <div className="mb-2 p-2 rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 flex items-start justify-between gap-2">
            <small className="text-xs text-gray-600 dark:text-gray-300">
              Replying to: {replyingTo.user} - {replyingTo.message?.substring(0, 100)}...
            </small>
            <button onClick={cancelReply} className="text-gray-400 hover:text-red-500 shrink-0">
              <FaTimesCircle className="w-4 h-4" />
            </button>
          </div>
        )}

        {selectedFiles.length > 0 && (
          <div className="mb-2 px-1 max-h-[120px] overflow-y-auto">
            <div className="flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => (
                <div key={`${file.name}-${index}`} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-neutral-800 border border-gray-200 dark:border-neutral-700">
                  <FaFile className="w-3 h-3 text-primary-500 shrink-0" />
                  <span className="text-xs truncate max-w-[120px]">{truncateFileName(file.name)}</span>
                  <button type="button" onClick={() => removeSelectedFile(index)} className="text-gray-400 hover:text-red-500">
                    <FaTimesCircle className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <label className="flex items-center gap-2 mb-2 text-xs text-gray-600 dark:text-gray-400 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={sendEmailOnSend}
            onChange={(e) => setSendEmailOnSend(e.target.checked)}
            className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
          />
          Also send email to client
        </label>

        <div className="flex items-end gap-2">
          <label htmlFor="employee-file-upload" className="p-2.5 rounded-xl text-gray-500 hover:text-primary-500 hover:bg-gray-100 dark:hover:bg-neutral-800 cursor-pointer transition-colors">
            <FaPaperclip className="w-4 h-4" />
            <input id="employee-file-upload" type="file" multiple onChange={handleFileChange} ref={fileInputRef} className="hidden" />
          </label>
          <textarea
            ref={textareaRef}
            value={newMessage}
            onChange={handleTextareaChange}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleMessageSend();
              }
            }}
            placeholder="Type your message..."
            className="flex-1 input-modern py-2.5 min-h-[42px] max-h-[120px] resize-none"
            rows="1"
          />
          <button
            onClick={handleMessageSend}
            disabled={isSending || (!newMessage.trim() && selectedFiles.length === 0)}
            className="p-2.5 rounded-xl bg-gradient-to-r from-primary-600 to-secondary-500 text-white hover:shadow-glow disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <IoMdSend className="w-5 h-5" />
          </button>
        </div>
      </div>

      {!isMobile && (
      <div
        className="absolute top-0 left-0 w-5 h-5 cursor-nw-resize rounded-br-md bg-white/10 hover:bg-white/25 transition-colors"
        onMouseDown={handleMouseDown}
        title="Drag to resize"
      />
      )}
      </div>
    </>,
    document.body
  );
};

export default EmployeeChatbox;